import { FormControlLabel, Grid, RadioGroup, useEventCallback } from '@mui/material';
import { AccordionGeneralityForm } from 'civil-status/view-models/accordion-generality-form';
import { Fragment, memo, useCallback, useEffect, useMemo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { FormikErrors, FormikTouched } from 'formik';
import { Divider } from '@mui/material';
import { FormControl } from '@mui/material';
import { Radio } from '@mui/material';
import { CodeTypeAct } from 'civil-status/constants/code-type-act';
import { PmiButton } from 'shell/components/shared/button';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { HttpResponse } from 'shell/models/http-response';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ActCategory } from 'shell/models/act-category-list';
import { ActListVM } from 'civil-status/view-models/research-category-form';
import { transformerActCategoryList } from 'civil-status/utils/transformer-act-category-list';
import Italian from '../../../shared/italian';
import { ItalianForm } from 'shell/view-models/italian-form';
import { toast } from 'react-toastify';

interface FiglioProps {
  disabled?: boolean;
  generality?: AccordionGeneralityForm;
  errorsGenerality?: FormikErrors<AccordionGeneralityForm>;
  touchedGenerality?: FormikTouched<AccordionGeneralityForm>;
  onChangeGenerality: (
    key: keyof AccordionGeneralityForm,
    value: AccordionGeneralityForm[keyof AccordionGeneralityForm]
  ) => void;
  onChangeFieldsGenerality: (values: AccordionGeneralityForm) => void;
}

const FiglioComponent = ({
  generality,
  onChangeGenerality,
  onChangeFieldsGenerality,
  touchedGenerality,
  errorsGenerality,
  disabled,
}: FiglioProps): React.ReactElement => {
  // risultato sempre un elemento, in futuro si può portare ad una lista se vogliono modificare la ricerca
  const [actList, getActList] = useHttpFetch<HttpResponse<ActCategory>, Array<ActListVM>>(
    `${__ACT_API_URL__}/consultazione/evento`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: transformerActCategoryList,
    }
  );

  const onSearch = useEventCallback(() => {
    getActList({
      data: {
        criteriRicercaEvento: {
          idAnsc: generality?.idActDigital || undefined,
        },
      },
    });

    toast.success('Operazione eseguita con successo');
  });

  const actListData = useMemo(() => {
    return (actList.data || []) as Array<ActListVM>;
  }, [actList.data]);

  useEffect(() => {
    if (actListData.length === 1) {
      const actItemSaved = actListData[0].itemSaved;

      onChangeGenerality('italianAct', {
        municipality: {
          description: actItemSaved?.nomeComuneRegistrazione,
          id: actItemSaved?.idComuneRegistrazione || '',
          code: actItemSaved?.idComuneRegistrazione
            ? Number.parseInt(actItemSaved?.idComuneRegistrazione, 10)
            : undefined,
          idProvince: actItemSaved?.idProvinciaRegistrazione
            ? Number.parseInt(actItemSaved?.idProvinciaRegistrazione, 10)
            : undefined,
          province: actItemSaved?.siglaProvinciaRegistrazione || '',
        },
      } as ItalianForm);
    }
  }, [actListData]);

  const onFieldItalianChange = useCallback(
    (key, value) => {
      onChangeGenerality('italianAct', {
        ...(generality || {}).italian,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldsItalianChange = useCallback(
    (valuesNew) => {
      onChangeGenerality('italianAct', {
        ...(generality || {}).italian,
        ...valuesNew,
      });
    },
    [generality, onChangeGenerality]
  );

  return (
    <Grid container spacing={2} item>
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Nuovo cognome"
          aria-label="Nuovo cognome"
          id="newSurname"
          name="newSurname"
          fullWidth
          type="text"
          variant="outlined"
          value={generality?.newSurname || ''}
          onChange={(event) => {
            onChangeGenerality('newSurname', event.target.value);
          }}
          error={touchedGenerality?.newSurname && Boolean(errorsGenerality?.newSurname)}
          helperText={errorsGenerality?.newSurname}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Tipo atto"
            id="typeAct"
            defaultValue={true}
            value={generality?.typeAct || ''}
            onChange={(event) =>
              onChangeFieldsGenerality({
                ...(generality || {}),
                typeAct: event.target.value || '',
                idActDigital: '',
                idActPaper: '',
              })
            }
          >
            <FormControlLabel
              value={CodeTypeAct.attoCartaceo}
              control={<Radio />}
              label="Atto cartaceo"
              disabled={disabled}
            />
            <FormControlLabel
              value={CodeTypeAct.attoDigitale}
              control={<Radio />}
              label="Atto digitale"
              disabled={disabled}
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid container spacing={2} item xs={12}>
        {generality?.typeAct === CodeTypeAct.attoDigitale ? (
          <Fragment>
            <Grid item xs={12} md={4}>
              <PmiTextField
                aria-label="ID Atto digitale"
                label="ID Atto digitale *"
                id="idActDigital"
                name="idActDigital"
                fullWidth
                type="text"
                variant="outlined"
                value={generality?.idActDigital || ''}
                onChange={(event) => {
                  onChangeGenerality('idActDigital', event.target.value);
                }}
                helperText={errorsGenerality?.idActDigital}
                error={touchedGenerality?.idActDigital && Boolean(errorsGenerality?.idActDigital)}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <PmiButton
                color="cdmBrandThird"
                variant="outlined"
                fullWidth
                onClick={onSearch}
                disabled={disabled || !Boolean(generality?.idActDigital)}
              >
                Cerca
              </PmiButton>
            </Grid>
          </Fragment>
        ) : (
          <Grid item xs={12} md={4}>
            <PmiTextField
              aria-label="ID Atto cartaceo"
              label="ID Atto cartaceo *"
              id="idActPaper"
              name="idActPaper"
              fullWidth
              type="text"
              variant="outlined"
              value={generality?.idActPaper || ''}
              onChange={(event) => {
                onChangeGenerality('idActPaper', event.target.value);
              }}
              helperText={errorsGenerality?.idActPaper}
              error={touchedGenerality?.idActPaper && Boolean(errorsGenerality?.idActPaper)}
              disabled={disabled}
            />
          </Grid>
        )}
      </Grid>
      <Italian
        disabledComponent={disabled}
        values={generality?.italianAct}
        onChange={onFieldItalianChange}
        onChanges={onFieldsItalianChange}
        errors={errorsGenerality?.italian as FormikErrors<ItalianForm>}
        touched={touchedGenerality?.italian as unknown as FormikTouched<ItalianForm>}
        labelMunicipality="Comune"
        labelProvince="Provincia"
        required
      />
      <Grid item xs={12}>
        <Divider />
      </Grid>
    </Grid>
  );
};

FiglioComponent.displayName = 'Form Figlio component';

const Figlio = memo(FiglioComponent);

export default Figlio;
